import React, { useState, useEffect, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { motion, useAnimation, useDragControls, AnimatePresence, useMotionValue, useTransform } from "framer-motion";

// Context for sharing state between BottomSheet and its sub-components
const BottomSheetContext = createContext(null);

/**
 * BottomSheet - A reusable mobile-friendly modal component
 * 
 * Features:
 * - Slides up from bottom on mobile, centered modal on desktop
 * - Pull-down-to-close gesture on mobile
 * - Backdrop with blur effect
 * - Spring animations for smooth transitions
 * - Responsive border radius (rounded top on mobile, fully rounded on desktop)
 * - Uses createPortal to render at document.body level
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal should close
 * @param {string} maxWidth - Max width on desktop: "sm" | "md" | "lg" | "xl" | "2xl" (default: "lg")
 * @param {boolean} showDragHandle - Show drag handle on mobile (default: true)
 * @param {boolean} closeOnBackdrop - Close when clicking backdrop (default: true)
 * @param {string} maxHeight - Max height of the modal (default: "85vh"). Pass "100vh" for full screen.
 * @param {React.ReactNode} children - Modal content
 */
export const BottomSheet = ({ 
    isOpen, 
    onClose, 
    maxWidth = "lg",
    maxHeight = "85vh",
    showDragHandle = true,
    closeOnBackdrop = true,
    disableDrag = false,
    children 
}) => {
    const controls = useAnimation();
    const dragControls = useDragControls();
    const [isClosing, setIsClosing] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Track drag Y offset for reactive backdrop opacity
    const dragY = useMotionValue(0);
    // Fade backdrop only when dragging down (dragY > 0); upward drag keeps opacity at 1
    const backdropOpacity = useTransform(dragY, (y) => {
        if (y <= 0) return 1;
        return Math.max(0, 1 - y / 300);
    });

    // Handle SSR - only render portal after mount
    useEffect(() => {
        setMounted(true);
    }, []);

    // Max width mapping
    const maxWidthClasses = {
        sm: "sm:max-w-sm",
        md: "sm:max-w-md",
        lg: "sm:max-w-lg",
        xl: "sm:max-w-xl",
        "2xl": "sm:max-w-2xl",
        full: "sm:max-w-full sm:mx-4",
    };

    // Start animation when modal opens
    useEffect(() => {
        if (isOpen && mounted) {
            setIsClosing(false);
            controls.start({ y: 0, opacity: 1 });
        }
    }, [isOpen, mounted, controls]);

    // Handle close with animation
    const handleClose = () => {
        setIsClosing(true);
        controls.start({ 
            y: "100%", 
            opacity: 0,
            transition: { duration: 0.2 }
        }).then(() => {
            onClose();
        });
    };

    // Handle drag end - close if dragged down enough
    const handleDragEnd = (event, info) => {
        const threshold = 100; // pixels to trigger close
        const velocity = info.velocity.y;
        
        if (info.offset.y > threshold || velocity > 500) {
            setIsClosing(true);
            controls.start({ 
                y: "100%", 
                opacity: 0,
                transition: { duration: 0.2 }
            }).then(() => {
                onClose();
            });
        } else {
            // Snap back - reset dragY so backdrop fades back in
            dragY.set(0);
            controls.start({ 
                y: 0,
                transition: { type: "spring", damping: 25, stiffness: 300 }
            });
        }
    };

    // Handle backdrop click
    const handleBackdropClick = () => {
        if (closeOnBackdrop) {
            handleClose();
        }
    };

    // Context value for sub-components
    const contextValue = {
        onClose: handleClose,
        dragControls,
        maxHeight,
    };

    // Don't render until mounted (SSR safety)
    if (!mounted) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <BottomSheetContext.Provider value={contextValue}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
                        onClick={handleBackdropClick}
                    >
                        {/* Backdrop - opacity tracks downward drag progress; upward drag has no effect */}
                        <motion.div 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                            style={{ opacity: backdropOpacity }}
                        />

                        {/* Modal Container */}
                        <motion.div
                            initial={{ y: "100%", opacity: 0 }}
                            animate={controls}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                            {...(!disableDrag ? {
                                drag: "y",
                                dragControls: dragControls,
                                dragConstraints: { top: 0, bottom: 0 },
                                dragElastic: { top: 0, bottom: 0.6 },
                                onDrag: (event, info) => dragY.set(info.offset.y),
                                onDragEnd: handleDragEnd,
                            } : {})}
                            className={`relative w-full ${maxWidthClasses[maxWidth] || maxWidthClasses.lg} bg-white rounded-t-3xl sm:rounded-2xl shadow-xl overflow-hidden ${disableDrag ? 'touch-auto' : 'touch-none sm:touch-auto'}`}
                            style={{ maxHeight }}
                        >
                            {/* Drag Handle */}
                            {showDragHandle && (
                                <div 
                                    className="sm:hidden flex flex-col items-center pt-2 pb-1 cursor-grab active:cursor-grabbing"
                                    onPointerDown={(e) => dragControls.start(e)}
                                >
                                    <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
                                </div>
                            )}

                            {/* Content */}
                            {children}
                        </motion.div>
                    </motion.div>
                </BottomSheetContext.Provider>
            )}
        </AnimatePresence>,
        document.body
    );
};

/**
 * BottomSheet.Header - Header section with optional close button
 * 
 * @param {React.ReactNode} children - Header content (title, subtitle, etc.)
 * @param {boolean} showCloseButton - Show close button (default: true)
 * @param {string} className - Additional CSS classes
 */
BottomSheet.Header = function BottomSheetHeader({ 
    children, 
    showCloseButton = true,
    className = "" 
}) {
    const context = useContext(BottomSheetContext);
    
    return (
        <div className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 ${className}`}>
            <div className="flex-1 min-w-0">
                {children}
            </div>
            {showCloseButton && (
                <button
                    onClick={context?.onClose}
                    className="ml-4 p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                    aria-label="Close"
                >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}
        </div>
    );
};

/**
 * BottomSheet.Title - Styled title component
 */
BottomSheet.Title = function BottomSheetTitle({ children, className = "" }) {
    return (
        <h2 className={`text-lg sm:text-xl font-semibold text-gray-900 ${className}`}>
            {children}
        </h2>
    );
};

/**
 * BottomSheet.Subtitle - Styled subtitle component
 */
BottomSheet.Subtitle = function BottomSheetSubtitle({ children, className = "" }) {
    return (
        <p className={`text-sm text-gray-500 mt-0.5 ${className}`}>
            {children}
        </p>
    );
};

/**
 * BottomSheet.Content - Scrollable content area
 * 
 * @param {React.ReactNode} children - Scrollable content
 * @param {string} className - Additional CSS classes
 * @param {boolean} noPadding - Remove default padding (default: false)
 */
BottomSheet.Content = function BottomSheetContent({ 
    children, 
    className = "",
    noPadding = false 
}) {
    const context = useContext(BottomSheetContext);
    // 166px accounts for drag handle (~20px) + header (~66px) + footer (~80px)
    const maxHeight = context?.maxHeight || '85vh';
    const contentMaxHeight = `calc(${maxHeight} - 166px)`;

    return (
        <div
            className={`overflow-y-auto overscroll-contain touch-auto ${noPadding ? '' : 'p-4'} ${className}`}
            style={{ maxHeight: contentMaxHeight }}
        >
            {children}
        </div>
    );
};

/**
 * BottomSheet.Footer - Footer section for action buttons
 * 
 * @param {React.ReactNode} children - Footer content (buttons, etc.)
 * @param {string} className - Additional CSS classes
 */
BottomSheet.Footer = function BottomSheetFooter({ children, className = "" }) {
    return (
        <div className={`px-4 sm:px-6 py-4 border-t border-gray-100 bg-white ${className}`}>
            {children}
        </div>
    );
};

/**
 * BottomSheet.CloseButton - A button that triggers animated close
 * Use this for Cancel/Done/Close buttons in Footer to ensure proper exit animation
 * 
 * @param {React.ReactNode} children - Button label (default: "Cancel")
 * @param {string} className - Additional CSS classes
 * @param {string} variant - Button style: "secondary" (gray) | "primary" (orange) | "danger" (red)
 */
BottomSheet.CloseButton = function BottomSheetCloseButton({ 
    children = "Cancel", 
    className = "",
    variant = "secondary"
}) {
    const context = useContext(BottomSheetContext);
    
    const variantClasses = {
        secondary: "text-gray-600 bg-gray-100 hover:bg-gray-200",
        primary: "text-white bg-linear-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-200",
        danger: "text-white bg-linear-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 shadow-lg shadow-red-200",
    };
    
    return (
        <button
            type="button"
            onClick={context?.onClose}
            className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-colors ${variantClasses[variant] || variantClasses.secondary} ${className}`}
        >
            {children}
        </button>
    );
};

/**
 * Custom hook to use BottomSheet context
 */
export const useBottomSheet = () => {
    const context = useContext(BottomSheetContext);
    if (!context) {
        throw new Error('useBottomSheet must be used within a BottomSheet component');
    }
    return context;
};

/**
 * Like useBottomSheet, but returns null outside a BottomSheet instead of
 * throwing - for components that render both inside a sheet and inline.
 */
export const useOptionalBottomSheet = () => useContext(BottomSheetContext);

export default BottomSheet;
